import { Lock, ChevronRight, FileSpreadsheet } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import EmptyState from '@/components/common/EmptyState'
import { ESTADO_HOJA } from './estados'

/**
 * EL PLAN — qué hojas hay, en qué orden se cargan y cuál está bloqueada.
 *
 * El orden NO es una preferencia de la pantalla: lo deriva el tenant del grafo de
 * dependencias de su propia especificación, y cargar las existencias antes que los
 * productos las rechaza todas. Aquí se enseña numerado, y una sección cuyas dependencias no
 * están dice EXACTAMENTE qué falta — "falta cargar Productos" — en vez de aparecer
 * deshabilitada sin explicación, que es lo que hace que el asesor crea que está rota.
 */
export default function PlanDeMigracion({ proyecto, orden = [], hojaActiva, onElegir }) {
  const hojas = proyecto?.hojas || []
  const posicion = (entidad) => {
    const i = orden.indexOf(entidad)
    return i === -1 ? orden.length : i
  }
  const ordenadas = [...hojas].sort((a, b) => posicion(a.entidad) - posicion(b.entidad))
  const cargadas = new Set(hojas.filter((h) => h.estado === 'cargada').map((h) => h.entidad))

  /** Qué le falta a esta hoja para poder cargarse: la primera entidad anterior sin cargar. */
  const bloqueadaPor = (hoja) => {
    if (!hoja.entidad || hoja.estado === 'cargada') return null
    const antes = orden.slice(0, posicion(hoja.entidad))
    const pendiente = antes.find((e) => hojas.some((h) => h.entidad === e) && !cargadas.has(e))
    return pendiente || null
  }

  const total = hojas.filter((h) => h.entidad && h.estado !== 'omitida').length
  const hechas = hojas.filter((h) => h.estado === 'cargada').length

  if (hojas.length === 0) {
    return (
      <EmptyState
        icono={<FileSpreadsheet size={28} />}
        titulo="Todavía no hay hojas"
        descripcion="Vuelve al paso Recopilar y suelta lo que te dio el cliente."
      />
    )
  }

  return (
    <div className="space-y-3">
      {total > 0 && (
        <div>
          <p className="text-caption text-neutral-500 dark:text-neutral-400">
            {hechas} de {total} secciones cargadas
          </p>
          <Progress value={total ? (hechas / total) * 100 : 0} className="mt-1" />
        </div>
      )}

      <ol className="space-y-2">
        {ordenadas.map((hoja, i) => {
          const estado = ESTADO_HOJA[hoja.estado] || ESTADO_HOJA.pendiente
          const falta = bloqueadaPor(hoja)
          const activa = hoja.hojaId === hojaActiva
          return (
            <li key={hoja.hojaId}>
              <Card
                onClick={() => onElegir?.(hoja.hojaId)}
                className={cn(
                  'cursor-pointer transition-colors hover:border-primary-300 dark:hover:border-primary-700',
                  activa && 'border-primary ring-1 ring-primary'
                )}
              >
                <CardContent className="flex items-center gap-3 py-3">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-neutral-100 dark:bg-neutral-800 text-caption font-semibold text-neutral-600 dark:text-neutral-400">
                    {i + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium text-neutral-900 dark:text-neutral-50">{hoja.nombreHoja}</p>
                    <p className="truncate text-caption text-neutral-500 dark:text-neutral-400">
                      {hoja.nombreArchivo} · {hoja.totalFilas?.toLocaleString('es-SV')} filas
                      {hoja.columnasSinUsar > 0 && ` · ${hoja.columnasSinUsar} columna(s) sin usar`}
                    </p>
                    {falta && (
                      <p className="flex items-center gap-1 text-caption text-warning-700 dark:text-warning-300">
                        <Lock size={12} /> falta cargar {falta}
                      </p>
                    )}
                  </div>
                  <Badge variant={estado.variante}>{estado.etiqueta}</Badge>
                  <ChevronRight size={16} className="shrink-0 text-neutral-300 dark:text-neutral-600" />
                </CardContent>
              </Card>
            </li>
          )
        })}
      </ol>
    </div>
  )
}
